/**
 * DemoModeBadge — the single demo-mode indicator for the app.
 *
 * Renders one of three variants driven by `DEMO_MODE`:
 *   • default → bold "DEMO MODE" pill (pulsing) for prominent surfaces
 *   • subtle  → small "Demo" chip for header chrome
 *   • banner  → dismissible info banner explaining demo data (session-scoped)
 *
 * Replaces the former standalone DemoDataBanner component.
 */

import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { Badge } from '@/components';
import { DEMO_MODE, DEMO_FEATURES } from '@/config/demoConfig';
import { useTranslation } from "react-i18next";

interface DemoModeBadgeProps {
  className?: string;
  variant?: 'default' | 'subtle' | 'banner';
  /** Banner only: the page under it is full width (Layout's FULL_WIDTH_ROUTES), so its text drops main's max-w-7xl column too. */
  fullWidth?: boolean;
}

const DemoModeBadge: React.FC<DemoModeBadgeProps> = ({
  className = '',
  variant = 'default',
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  // Banner dismissal is session-scoped — keep it inside the banner variant only.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    if (variant !== 'banner') return;
    if (sessionStorage.getItem('demo-banner-dismissed') === 'true') {
      setBannerDismissed(true);
    }
  }, [variant]);

  if (!DEMO_MODE) {
    return null;
  }

  // ── Banner variant ──────────────────────────────────────────────────────
  if (variant === 'banner') {
    // Respect both the global feature flag and per-session dismissal.
    if (!DEMO_FEATURES.showWarningBanner || bannerDismissed) {
      return null;
    }

    const handleDismiss = () => {
      setBannerDismissed(true);
      sessionStorage.setItem('demo-banner-dismissed', 'true');
    };

    return (
      <div
        className={`bg-accent-50 border-b border-accent-200 px-4 py-3 ${className}`}
        role="alert"
        aria-label={t("misc.demoNotification")}
      >
        <div className={`${fullWidth ? '' : 'max-w-7xl '}mx-auto flex items-start justify-between`}>
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-accent-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-accent-900">
                {t("misc.demoViewing")}
              </p>
              <p className="text-sm text-accent-700 mt-1">
                {t('misc.demoBody')}
                {DEMO_FEATURES.allowLiveConnections && (
                  <span className="ml-2">
                    {t('misc.demoConnect')}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-accent-700 hover:text-accent-900 transition-colors p-1 rounded hover:bg-accent-100"
            aria-label={t("misc.demoDismiss")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // ── Pill variants (default + subtle) ────────────────────────────────────
  const isSubtle = variant === 'subtle';

  return (
    <Badge
      variant={isSubtle ? 'secondary' : 'warning'}
      size="sm"
      className={`${className} ${isSubtle ? 'bg-accent-100 text-accent-800 border-accent-200' : 'animate-pulse'}`}
    >
      {isSubtle ? t('misc.demoBadgeShort') : t('misc.demoBadgeLong')}
    </Badge>
  );
};

export default DemoModeBadge;
